import { ReactNode, useEffect, useState } from "react";

/**
 * Piano Intermediate — bespoke design language.
 *
 * Visual identity:
 *   • Deep cobalt + warm amber (distinct from MT amber/purple, ET emerald,
 *     song-courses slate, SIS saffron, PF blue)
 *   • Geometric grid background — "practice studio" aesthetic
 *   • Live practice meter at the top of every lesson — gentle pulsing dot
 *     that mirrors the lesson's BPM
 *   • Exercises framed as "Drills" rather than "Chapters" — vocabulary shift
 *     that reinforces this is a workout
 */

interface PIShellProps {
  title: string;
  description?: string;
  exerciseGoal: string;
  bpm?: number;
  difficulty?: "Beginner+" | "Intermediate" | "Advanced";
  children: ReactNode;
}

export function PIShell({ title, description, exerciseGoal, bpm = 80, difficulty = "Intermediate", children }: PIShellProps) {
  return (
    <div className="min-h-screen pb-16">
      {/* Geometric "studio floor" backdrop — pure CSS, distinctive to PI */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background: `
            radial-gradient(800px 400px at 80% -10%, rgba(30,58,138,0.20), transparent 60%),
            radial-gradient(900px 420px at -10% 110%, rgba(245,158,11,0.10), transparent 60%),
            #0a0e1a
          `,
          backgroundImage: `
            linear-gradient(rgba(30,58,138,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(30,58,138,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
        }}
      />

      {/* HERO */}
      <section className="relative border-b border-cyan-500/15 bg-gradient-to-b from-cyan-950/40 via-transparent to-transparent">
        <div className="mx-auto max-w-5xl px-4 py-10 lg:px-8 lg:py-14">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.32em] text-amber-400">
                <BPMDot bpm={bpm} />
                Piano Intermediate · Drill Studio
              </div>
              <h1
                className="mt-2 text-3xl font-semibold leading-tight text-cyan-50 lg:text-5xl"
                style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
              >
                {title}
              </h1>
              {description && (
                <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-cyan-100/80">{description}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 text-[11px]">
              <Tag label="Goal" value={exerciseGoal} accent="amber" />
              <Tag label="Tempo" value={`${bpm} bpm`} accent="cyan" mono />
              <Tag label="Level" value={difficulty} accent="rose" />
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-10 lg:px-8 lg:py-12">{children}</main>
    </div>
  );
}

function Tag({ label, value, accent, mono }: { label: string; value: string; accent: "amber"|"cyan"|"rose"; mono?: boolean }) {
  const cls = {
    amber: "border-amber-400/40 bg-amber-500/10 text-amber-200",
    cyan:  "border-cyan-400/40 bg-cyan-500/10 text-cyan-200",
    rose:  "border-rose-400/40 bg-rose-500/10 text-rose-200",
  }[accent];
  return (
    <span className={`flex items-baseline gap-1.5 rounded-full border px-3 py-1 ${cls}`}>
      <span className="text-[9px] font-bold uppercase tracking-[0.2em] opacity-70">{label}</span>
      <span className={mono ? "font-mono" : "font-semibold"}>{value}</span>
    </span>
  );
}

/** Live "metronome dot" — pulses at the lesson's BPM. Subtle, never distracting. */
function BPMDot({ bpm }: { bpm: number }) {
  const ms = (60_000 / bpm).toFixed(0);
  return (
    <span
      className="inline-block h-2 w-2 rounded-full bg-amber-400"
      style={{ animation: `pi-bpm-pulse ${ms}ms ease-in-out infinite` }}
    />
  );
}

/* Exercise card — replaces the dark `Chapter` for PI lessons. */
export function Drill({
  number,
  label,
  title,
  children,
}: {
  number?: string | number;
  label?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-cyan-950/40 via-zinc-900/40 to-amber-950/20 p-5 shadow-[0_8px_40px_rgba(0,0,0,0.45)] backdrop-blur-sm">
      {number != null && (
        <div
          className="pointer-events-none absolute -right-2 -top-1 select-none text-[120px] font-bold leading-none text-cyan-400/[0.06]"
          style={{ fontFamily: '"Cormorant Garamond", serif' }}
        >
          {String(number).padStart(2, "0")}
        </div>
      )}
      <div className="relative">
        {label && (
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-400">
            ⚡ {label}
          </div>
        )}
        {title && (
          <h2
            className="mt-1 text-2xl font-semibold leading-tight text-cyan-50 lg:text-3xl"
            style={{ fontFamily: '"Cormorant Garamond", "Spectral", Georgia, serif' }}
          >
            {title}
          </h2>
        )}
        <div className="mt-3 space-y-3 text-[15.5px] leading-[1.78] text-cyan-50/95">{children}</div>
      </div>
    </section>
  );
}

/** Bullet list of practice steps — numbered, distinct from MT's Steps. */
export function DrillSteps({ children }: { children: ReactNode }) {
  return (
    <ol className="my-3 space-y-2 [counter-reset:dr]">
      {children}
    </ol>
  );
}

export function DrillStep({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-3 [counter-increment:dr] before:mt-0.5 before:flex before:h-7 before:w-7 before:flex-shrink-0 before:items-center before:justify-center before:rounded-full before:bg-amber-500/20 before:text-sm before:font-bold before:text-amber-300 before:content-[counter(dr)]">
      <div className="flex-1 text-[15px] leading-relaxed text-cyan-50/95">{children}</div>
    </li>
  );
}

/** Why-it-matters callout — amber rule, deep navy fill. */
export function PIInsight({ children }: { children: ReactNode }) {
  return (
    <aside className="my-3 rounded-xl border-l-4 border-amber-400/80 bg-cyan-950/40 px-4 py-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
        ✦ Why this drill matters
      </div>
      <div className="text-[15px] leading-relaxed text-cyan-50/95">{children}</div>
    </aside>
  );
}

/** Live count-up practice timer with target time and gentle BPM clicker. */
export function PracticeTimer({ targetMinutes = 5, bpm }: { targetMinutes?: number; bpm?: number }) {
  const [secs, setSecs] = useState(0);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [running]);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  const target = targetMinutes * 60;
  const pct = Math.min(100, (secs / target) * 100);
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-cyan-950/30 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ⏱ Practice timer · target {targetMinutes} min
        </div>
        {bpm && <div className="text-[11px] font-mono text-cyan-300">{bpm} bpm</div>}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            if (running) { setRunning(false); }
            else { setRunning(true); }
          }}
          className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
        >
          {running ? "■ Pause" : "▶ Start"}
        </button>
        <button
          onClick={() => { setRunning(false); setSecs(0); }}
          className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 py-2 text-sm font-semibold text-cyan-200 hover:bg-cyan-500/20"
        >
          ↻ Reset
        </button>
        <div className="ml-auto font-mono text-3xl text-cyan-50 tabular-nums">
          {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </div>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-cyan-900/60">
        <div
          className="h-full bg-gradient-to-r from-cyan-400 to-amber-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Multi-stage practice ladder. Each lesson supplies its own rungs.
 *  Auto-advances to the next rung when the timer hits the rung's target,
 *  unless the student presses "Stay on this rung". The bottom progress bar
 *  shows all rungs proportionally so the whole journey is visible. */

export type LadderAccent = "amber" | "cyan" | "rose" | "emerald" | "violet";
export interface LadderRung {
  title: string;
  detail: string;
  minutes: number;
  accent?: LadderAccent;
}

const LADDER_ACCENT: Record<LadderAccent, { bar: string; chip: string; ring: string }> = {
  amber:   { bar: "from-amber-500 to-amber-300",     chip: "bg-amber-500/20 text-amber-200",   ring: "ring-amber-400/60" },
  cyan:    { bar: "from-cyan-400 to-cyan-200",       chip: "bg-cyan-500/20 text-cyan-100",     ring: "ring-cyan-300/60" },
  rose:    { bar: "from-rose-500 to-rose-300",       chip: "bg-rose-500/20 text-rose-200",     ring: "ring-rose-400/60" },
  emerald: { bar: "from-emerald-500 to-emerald-300", chip: "bg-emerald-500/20 text-emerald-200", ring: "ring-emerald-400/60" },
  violet:  { bar: "from-violet-500 to-violet-300",   chip: "bg-violet-500/20 text-violet-200", ring: "ring-violet-400/60" },
};

export function PracticeLadder({
  rungs,
  intro,
}: {
  rungs: LadderRung[];
  /** Optional explainer line shown above the ladder. */
  intro?: ReactNode;
}) {
  const [running, setRunning] = useState(false);
  const [holding, setHolding] = useState(false);
  const [rungIdx, setRungIdx] = useState(0);
  const [rungSecs, setRungSecs] = useState(0);

  const totalSecs = rungs.reduce((s, r) => s + r.minutes * 60, 0);
  const elapsedBefore = rungs.slice(0, rungIdx).reduce((s, r) => s + r.minutes * 60, 0);
  const totalElapsed = elapsedBefore + rungSecs;

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setRungSecs((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);

  useEffect(() => {
    const target = rungs[rungIdx].minutes * 60;
    if (rungSecs >= target && !holding && rungIdx < rungs.length - 1) {
      setRungIdx((i) => i + 1);
      setRungSecs(0);
    } else if (rungSecs >= target && rungIdx === rungs.length - 1) {
      setRunning(false);
    }
  }, [rungSecs, rungIdx, holding, rungs]);

  const reset = () => {
    setRunning(false);
    setHolding(false);
    setRungIdx(0);
    setRungSecs(0);
  };
  const fmt = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const current = rungs[rungIdx];
  const accent = current.accent ?? "amber";
  const c = LADDER_ACCENT[accent];
  const rungProgress = (rungSecs / (current.minutes * 60)) * 100;

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-cyan-950/30 p-4">
      {intro && <div className="mb-3 text-[14px] text-cyan-100/80">{intro}</div>}

      <div className={`rounded-xl p-4 ring-1 ${c.ring} bg-gradient-to-br from-cyan-950/60 to-zinc-900/40`}>
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${c.chip}`}>
              Rung {rungIdx + 1} of {rungs.length}
            </div>
            <h3
              className="mt-1.5 text-[22px] font-semibold leading-tight text-cyan-50"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              {current.title}
            </h3>
            <p className="mt-1 max-w-2xl text-[14px] leading-snug text-cyan-100/80">{current.detail}</p>
          </div>
          <div className="font-mono text-3xl tabular-nums text-cyan-50">{fmt(rungSecs)}</div>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-zinc-900/70">
          <div className={`h-full bg-gradient-to-r ${c.bar} transition-[width] duration-200`} style={{ width: `${Math.min(100, rungProgress)}%` }} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setRunning((r) => !r)}
          className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
        >
          {running ? "■ Pause" : rungIdx === 0 && rungSecs === 0 ? "▶ Start the ladder" : "▶ Resume"}
        </button>
        <button
          onClick={() => setHolding((h) => !h)}
          disabled={!running}
          className={`rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-40 ${
            holding ? "border-rose-400/60 bg-rose-500/15 text-rose-200" : "border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
          }`}
        >
          {holding ? "● Holding (release)" : "⤓ Stay on this rung"}
        </button>
        <button
          onClick={() => {
            if (rungIdx < rungs.length - 1) {
              setRungIdx((i) => i + 1);
              setRungSecs(0);
            }
          }}
          disabled={rungIdx >= rungs.length - 1}
          className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 px-3 py-2 text-sm font-semibold text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-40"
        >
          Next rung →
        </button>
        <button
          onClick={reset}
          className="ml-auto rounded-lg border border-zinc-600 bg-zinc-800/60 px-3 py-2 text-xs font-semibold text-zinc-300 hover:bg-zinc-700/60"
        >
          ↻ Restart
        </button>
      </div>

      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-cyan-300/80">
          <span>Whole ladder</span>
          <span className="font-mono text-cyan-200">{fmt(totalElapsed)} / {fmt(totalSecs)}</span>
        </div>
        <div className="flex h-2 overflow-hidden rounded-full bg-zinc-900/70">
          {rungs.map((r, i) => {
            const isPast = i < rungIdx;
            const isNow = i === rungIdx;
            const fill = isPast ? 100 : isNow ? rungProgress : 0;
            const cc = LADDER_ACCENT[r.accent ?? "amber"];
            return (
              <div key={i} className="relative h-full" style={{ flex: r.minutes }}>
                <div className={`h-full bg-gradient-to-r ${cc.bar} transition-[width] duration-200`} style={{ width: `${Math.min(100, fill)}%` }} />
              </div>
            );
          })}
        </div>
        <div className="mt-1 flex text-[10px] uppercase tracking-wider text-cyan-300/60">
          {rungs.map((r, i) => (
            <span key={i} className="px-1 truncate" style={{ flex: r.minutes }}>
              {i + 1}. {r.title}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Inline note image with cobalt frame instead of cream (matches PI theme). */
export function PINoteImage({
  src,
  caption,
}: {
  src: string;
  caption?: string;
}) {
  return (
    <figure className="my-5 overflow-hidden rounded-2xl border border-cyan-400/20 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
      <img src={src} alt={caption ?? "Handwritten practice note"} className="block w-full" loading="lazy" />
      {caption && (
        <figcaption className="border-t border-cyan-400/15 bg-cyan-950/40 px-4 py-2 text-[12px] italic text-cyan-200">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
